import { useEffect, useState } from "react";
import { InnerGlowLogo } from "./InnerGlowLogo";

const ImdbIcon = ({ size = 28 }) => (
  <svg
    width={size}
    height={(size * 14) / 28}
    viewBox="0 0 64 32"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    className="fill-current"
  >
    <rect x="0" y="0" width="64" height="32" rx="3" />
    <g fill="#000">
      <rect x="5" y="9" width="3" height="14" />
      <path d="M11 9h4l1.5 7L18 9h4v14h-3v-9l-2 9h-2l-2-9v9h-2z" />
      <path d="M25 9h4c2.2 0 3 .8 3 3v8c0 2.2-.8 3-3 3h-4V9zm3 2v10h1c.5 0 .7-.2.7-.7v-8.6c0-.5-.2-.7-.7-.7h-1z" />
      <path d="M35 9h3v3.5c.4-.6 1.1-1 2-1 1.5 0 2.2.8 2.2 2.7v6.6c0 1.9-.7 2.7-2.2 2.7-.9 0-1.6-.4-2-1V23h-3V9zm3 6.5v5c0 .4.2.6.5.6s.5-.2.5-.6v-5c0-.4-.2-.6-.5-.6s-.5.2-.5.6z" />
      <path d="M46 9h2.7l.5 4 .6-4H53v14h-2.5v-9l-.9 9h-2L46.7 14v9H46z" />
    </g>
  </svg>
);

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const navLink = (href, label, id, opts = {}) => (
    <a
      href={href}
      data-testid={id}
      onClick={() => setOpen(false)}
      target={opts.external ? "_blank" : undefined}
      rel={opts.external ? "noopener noreferrer" : undefined}
      className="link-line font-sans text-[12px] uppercase tracking-[0.24em] text-foreground/75 hover:text-foreground transition-colors font-light"
    >
      {label}
    </a>
  );

  return (
    <header
      data-testid="nav-root"
      className={`fixed top-0 left-0 right-0 z-50 nav-glass transition-all duration-500 ${
        scrolled
          ? "bg-background/75 border-b border-border"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
        <a href="#hero" data-testid="nav-logo" className="text-foreground">
          <InnerGlowLogo />
        </a>

        <nav className="hidden md:flex items-center gap-10">
          {navLink("#hero", "About", "nav-link-about")}
          {navLink("#work", "Work", "nav-link-services")}
          {navLink("#contact", "Contact", "nav-link-contact")}
          <a
            href="https://www.imdb.com/name/nm10173414/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-link-imdb"
            aria-label="Gregory Bado on IMDb"
            className="flex items-center gap-2 text-foreground/75 hover:text-[#F5C518] transition-colors"
            title="View Gregory Bado on IMDb"
          >
            <span className="text-[#F5C518]">
              <ImdbIcon size={32} />
            </span>
          </a>
        </nav>

        <div className="flex items-center gap-3 md:gap-5">
          <a
            href="#contact"
            data-testid="nav-cta-btn"
            className="hidden md:inline-flex items-center gap-2 px-5 h-9 border border-foreground/80 hover:bg-foreground hover:text-background font-sans text-[11px] uppercase tracking-[0.24em] transition-colors font-light"
          >
            Start a Project
          </a>

          <button
            data-testid="nav-mobile-toggle"
            aria-label="Menu"
            onClick={() => setOpen((v) => !v)}
            className="md:hidden w-9 h-9 flex flex-col items-center justify-center gap-[5px] border border-border"
          >
            <span className={`block w-4 h-px bg-foreground transition-transform ${open ? "translate-y-[3px] rotate-45" : ""}`} />
            <span className={`block w-4 h-px bg-foreground transition-transform ${open ? "-translate-y-[3px] -rotate-45" : ""}`} />
          </button>
        </div>
      </div>

      <div className={`md:hidden overflow-hidden transition-[max-height] duration-500 ease-out border-b border-border ${open ? "max-h-96" : "max-h-0"}`}>
        <div className="px-6 py-6 flex flex-col gap-5 bg-background/95">
          {navLink("#hero", "About", "nav-link-about-mobile")}
          {navLink("#work", "Work", "nav-link-services-mobile")}
          {navLink("#contact", "Contact", "nav-link-contact-mobile")}
          <a
            href="https://www.imdb.com/name/nm10173414/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-link-imdb-mobile"
            onClick={() => setOpen(false)}
            className="font-sans text-[12px] uppercase tracking-[0.24em] text-[#F5C518] font-light inline-flex items-center gap-2"
          >
            <ImdbIcon size={32} />
            <span>IMDb</span>
          </a>
          <a
            href="#contact"
            onClick={() => setOpen(false)}
            data-testid="nav-cta-mobile-btn"
            className="inline-flex w-fit items-center gap-2 px-5 h-9 border border-foreground/80 font-sans text-[11px] uppercase tracking-[0.24em] font-light"
          >
            Start a Project
          </a>
        </div>
      </div>
    </header>
  );
};
